import React, { Fragment, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import merge from 'lodash/merge';
import update from 'lodash/update';
import LinkButton from '@/se/components/LinkButton';
import Button from '@/se/components/Button[DEPRECATED]';
// @ts-ignore
import waiting from '../../../../../assets/images/waiting.svg';
import { STATUSES as ROOM_STATUSES } from '../../../../entities/room/enums';
import { getPatientProcedureDuration, transformORPatients } from '../../../../entities/patient/transducers';
import { mapTabletOperationRoom } from '../../../../entities/room/transducers';
import { format } from 'date-fns';

import {
  opreationRoomSubscription,
  setPatientStatus,
  skipCleaning as skipCleaningMutation,
  turnoverClockOff as turnoverClockOffMutation,
  orRoomReady as orRoomReadyMutation,
} from '../../../../../graph/rooms';
import {
  exitProcedure as exitProcedureMutation,
  editOrTimes as editOrTimesMutation,
  setFlipRoom,
} from '../../../../../graph/patients';
import { setProcedureStepTimestamp as setProcedureStepTimestampMutation } from '../../../../../graph/procedureSteps';
import { useMutation, useQuery, useSubscription, useApolloClient } from '@apollo/client';
import { useSetRoomMutation } from '@/hooks/useSetRoomMutation';
import ClientUpdater from '../../../../ClientUpdater';
import Troubleshooting from '../Troubleshooting/Troubleshooting';
import { CenteredSpinner } from '@/se/components/Spinner';
import { AirFiltrationIndicatorTimer } from '../../widgets/AirFiltrationIndicator';
import { Modal, ModalAction, Subtitle, Title } from '../Modal';
import PatientSelection from '../../nursingApp/PatientSelection';
import PatientsTodaySelection from '../../nursingApp/PatientsTodaySelection';
import { getProcedures as getProcedureCards } from '../../schedule/transform';
import { listSubscription as proceduresSubscription } from '../../../../../graph/procedures';
import UpcomingProcedure from '../../schedule/overview/Procedure';
import Box from '@material-ui/core/Box';
import { makeStyles, ThemeProvider } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import { getNextScheduledProcedures } from '../procedures';
import { getRemainingScheduledProcedures } from '../proceduresRemaining';
import { Alert } from '@material-ui/lab';
import { Patient, PatientStatus } from '@/types/Patient';
import { orTabletProcedureSteps, procedureSteps } from '@/graph/procedureSteps';
import { RouteComponentProps } from 'react-router';
import { Room } from '@/types/Room';
import CurrentProcedure from './CurrentProcedure';
import { tabletThemeDark } from '../../../../../themes/tabletTheme';
import { getNestedValue } from '@/se/utilities/data/object';
import responsiveStyles from '@/se/utilities/responsive';
import { ButtonLabel, MainButton } from '../common';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import CheckIcon from '@material-ui/icons/Check';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import TodayIcon from '@material-ui/icons/Today';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { ProcedureStep } from '@/types/ProcedureStep';
import { useScreenConfiguration } from '@/contexts/ScreenConfigurationContext';
import { useWebSocketReconnect } from '@/hooks/useWebSocketReconnect';
import { useToaster } from '@/components/core/Toaster';
import { isFlipRoomFn, getLogEntries, patientEnteredTime, eventTime } from '../utils';
import { PROCEDURE_STATUSES } from '../../../../entities/patient/enums';
import DelayReasonModal, {
  DelayReasonOption,
  getDelayReasonLabel,
  DEFAULT_DELAY_REASONS,
} from './DelayReasonModal';
import {
  isDelayReasonRequired,
  getProcedureDelayReason,
  submitDelayReason,
  skipDelayReason,
  updateDelayReason,
} from '@/graph/procedureDelayReason';
import { list as configList } from '@/graph/config';
import { ConfigType } from '@/types/HospitalConfig';

interface OrRoomTimes {
  enteredAt: string | null;
  timeoutStart: string | null;
  procedureStart: string | null;
  calledNextPatient: string | null;
  closingStart: string | null;
  surgeonLeft: string | null;
}

interface ExtendedPatient extends Patient {
  orRoomTimes?: OrRoomTimes;
  name: string;
}

interface PreviousState {
  patient: ExtendedPatient;
  steps: ProcedureStep[];
  status: PatientStatus;
  timestamp: number;
  isFlipRoomActive: boolean;
}

const MOVE_BACK_WINDOW = 5 * 60 * 1000; // 5 minutes

const MessageToSend = styled.div`
  position: relative;
  padding: 1em 1.5em;
  border-radius: .25em;
  margin-bottom: 4rem;
  font-size: 1.75rem;
  line-height: 1.5;
  background-color: ${props => props.theme.textColor.alpha(0.2).string()};
  max-width: 30em;
  text-align: left;
  white-space: pre-wrap;

  :before {
    content: "";
    width: 0;
    height: 0;
    position: absolute;
    border-left: 10px solid transparent;
    border-right: 10px solid ${props => props.theme.textColor.alpha(0.2).string()};
    border-top: 10px solid ${props => props.theme.textColor.alpha(0.2).string()};
    border-bottom: 10px solid transparent;
    right: 19px;
    bottom: -20px;
  }
}
`;

const Root = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;
  color: ${props => props.theme.textColor.string()};
  font-family:
    -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif, 'Apple Color Emoji',
    'Segoe UI Emoji', 'Segoe UI Symbol';
`;

export const Idle = styled.div`
  margin-top: auto;
  margin-bottom: auto;
  text-align: center;
  grid-column: 1 / span 2;
`;

export const Illustration = styled.img`
  max-width: 6rem;
`;

export const Supertitle = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  margin-top: 1.5rem;
  opacity: 0.5;

  @media (min-width: 50rem) {
    font-size: 1.25rem;
  }
`;

const SubtleLink = styled(LinkButton)`
  color: inherit;
  opacity: 0.5;
  margin-bottom: 0.5rem;
`;

const responsive = responsiveStyles as any;

const Actions = styled.div`
  display: flex;
  padding: 0 1.75rem;

  ${responsive.sm.andSmaller`
    flex-flow: column-reverse;
    padding: 0 .25rem;
  `};
`;

const ActionsAlt = styled(Actions)`
  grid-column: 1 / span 2;
  padding-bottom: 2.5rem;
`;

const MoveBackButton = styled(Button)`
  min-width: auto;
  padding: 12px 24px;
  font-size: 1.1rem;
  text-transform: none;
  border-radius: 8px;
  margin-top: 16px;

  .MuiButton-label {
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

function modalOnClick(e: MouseEvent) {
  e.preventDefault();
  e.stopPropagation();
}

const modalStyle = { overflowY: 'unset', fontWeight: 400 };

export const CustomModal = (message = '', yesPrimary = true, yesLabel = '', yesSecondaryLabel = '', noLabel = '') => {
  const CustomModalContent = ({
    handleConfirm,
    handleSecondaryConfirm,
    handleCancel,
    working,
    sendTo,
    messageToSend,
    errorMessage,
    children,
  }: any) => (
    <Modal onClick={modalOnClick} style={modalStyle}>
      <Title>Are you sure?</Title>
      <Subtitle>
        {message}{' '}
        {!!sendTo && (
          <span>
            <br />
            {sendTo}
          </span>
        )}
      </Subtitle>
      {!!messageToSend && <MessageToSend>{messageToSend}</MessageToSend>}
      {children}
      <ModalAction>
        <Button xl primary={yesPrimary} onClick={handleConfirm} disabled={working}>
          <WithWorking working={working}>{yesLabel || 'Yes'}</WithWorking>
        </Button>
        {!!handleSecondaryConfirm && (
          <Button xl primary={yesPrimary} onClick={handleSecondaryConfirm} disabled={working}>
            <WithWorking working={working}>{yesSecondaryLabel || 'Yes'}</WithWorking>
          </Button>
        )}
        <Button xl primary={!yesPrimary} onClick={handleCancel} disabled={working}>
          {noLabel || 'No'}
        </Button>
      </ModalAction>
      {!!errorMessage && (
        <Alert severity="error" style={{ marginTop: 40 }}>
          {errorMessage}
        </Alert>
      )}
    </Modal>
  );

  CustomModalContent.displayName = 'CustomModal';

  return CustomModalContent;
};

const ExitModal = CustomModal('Current procedure progress will be lost. This action cannot be reversed.');
const ClosingModal = CustomModal(
  'This action will start procedure closing and send SMS to the patient’s caretaker.',
  true
);

export const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flex: 1,
    justifyContent: 'center',
  },
  dialogContent: {
    background: theme.palette.background.default,
    fontSize: '1.5em',
  },
  appBar: {
    position: 'relative',
  },
  title: {
    marginLeft: theme.spacing(2),
    flex: 1,
  },
}));

export const WithWorking = ({
  working,
  children,
  size = null,
  spinnerStyle,
}: {
  size?: any;
  working: boolean;
  children: ReactNode;
  spinnerStyle?: React.CSSProperties;
}) => (
  <Box display="grid" alignItems="center">
    <Box
      gridRow={1}
      gridColumn={1}
      visibility={working ? 'visible' : 'hidden'}
      style={{ marginTop: '-1.25em', ...spinnerStyle }}
    >
      {working ? <CenteredSpinner size={size} /> : null}
    </Box>
    <Box gridRow={1} gridColumn={1} visibility={working ? 'hidden' : 'visible'}>
      {children}
    </Box>
  </Box>
);

const ActionWrapper = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  margin: 0 auto;
  bottom: 2rem;
`;

const OperationRoomTabletV2 = ({ match }: RouteComponentProps<{ operationRoomId: string }>) => {
  const roomId = parseInt(match?.params?.operationRoomId, 10);
  const client = useApolloClient();

  // Track WebSocket reconnections for logging and debugging
  const reconnectCount = useWebSocketReconnect();
  const prevReconnectCount = useRef(reconnectCount);

  // Log reconnection events for debugging production issues
  useEffect(() => {
    if (reconnectCount > 0 && reconnectCount !== prevReconnectCount.current) {
      console.log(`[ORTabletV2] WebSocket reconnected (count: ${reconnectCount}), subscriptions will auto-resubscribe`);
      prevReconnectCount.current = reconnectCount;
    }
  }, [reconnectCount]);

  const [modal, setModal] = useState<boolean>(false);
  const [closingModal, setClosingModal] = useState<boolean>(false);
  const [patientsModal, setPatientsModal] = useState<boolean>(false);
  const [patientsTodayModal, setPatientsTodayModal] = useState<boolean>(false);
  const [previousState, setPreviousState] = useState<PreviousState | null>(null);
  const [isWithinTimeWindow, setIsWithinTimeWindow] = useState<boolean>(true);
  const [isMovingPatientBack, setIsMovingPatientBack] = useState<boolean>(false);
  const [userRequestedDelayReasonModal, setUserRequestedDelayReasonModal] = useState(false);
  const [optimisticDelayReason, setOptimisticDelayReason] = useState<{ id: string; description?: string } | null>(null);
  const [optimisticDelayReasonSkipped, setOptimisticDelayReasonSkipped] = useState<boolean | null>(null);
  const [lastCreatedDelayReasonId, setLastCreatedDelayReasonId] = useState<number | null>(null);

  const toaster = useToaster();
  const [setRoomForPatient] = useSetRoomMutation();
  const [exitProcedure] = useMutation(exitProcedureMutation);
  const [orRoomReady] = useMutation(orRoomReadyMutation);
  const [editOrTimes] = useMutation(editOrTimesMutation);
  const [setProcedureStepTimestamp] = useMutation(setProcedureStepTimestampMutation);
  const [setStatus] = useMutation(setPatientStatus);
  const [doFlipRoom] = useMutation(setFlipRoom, {
    onCompleted: () => { },
  });
  const [doSubmitDelayReason] = useMutation(submitDelayReason);
  const [doSkipDelayReason] = useMutation(skipDelayReason);
  const [doUpdateDelayReason] = useMutation(updateDelayReason);

  // shouldResubscribe: true ensures subscription re-establishes after WebSocket reconnection
  const roomData = useSubscription(opreationRoomSubscription, {
    variables: {
      roomId,
    },
    shouldResubscribe: true,
  });

  const room: Room & { status: string; patient: Patient & { duration?: string } } = useMemo(() => {
    const transformedRoom = transformORPatients(roomData?.data?.tabletApp || {});
    const update1 = update(transformedRoom, 'patient', patient =>
      merge(patient, getPatientProcedureDuration(patient, transformedRoom))
    );
    const update2 = mapTabletOperationRoom(update1);
    return {
      ...update2,
      status: !update2.patient ? null : update2.status,
    };
  }, [roomData]);

  const roomPatientId = room?.patient?.id;
  const procedureId = room?.patient?.procedureId ?? room?.patient?.procedure?.id;

  const { data: delayReasonConfigData } = useQuery(configList, {
    variables: { type: ConfigType.ProcedureDelayReason },
  });

  const configuredDelayReasons: DelayReasonOption[] = useMemo(() => {
    const configs = delayReasonConfigData?.configs || [];
    if (configs.length === 0) {
      return DEFAULT_DELAY_REASONS as unknown as DelayReasonOption[];
    }
    const reasons = configs.map((config: { id: number; values: string }) => {
      const parsed = JSON.parse(config.values || '{}');
      const name = parsed.name || 'Unknown';
      return { id: name, label: name };
    });
    const hasOther = reasons.some((r: DelayReasonOption) => r.id.toLowerCase() === 'other');
    if (!hasOther) {
      reasons.push({ id: 'other', label: 'Other' });
    }
    return reasons;
  }, [delayReasonConfigData]);

  const { data: delayRequiredData } = useQuery(isDelayReasonRequired, {
    variables: { procedureId, operationRoomId: roomId },
    skip: !procedureId,
    fetchPolicy: 'network-only',
  });

  const { data: existingDelayReasonData, refetch: refetchExistingDelayReason } = useQuery(getProcedureDelayReason, {
    variables: { procedureId },
    skip: !procedureId,
    fetchPolicy: 'network-only',
  });

  const delayRequiredInfo = delayRequiredData?.isDelayReasonRequired;
  const existingDelayReason = existingDelayReasonData?.procedureDelayReason;

  const delayReason = useMemo(() => {
    if (existingDelayReason?.reason) {
      const [id, ...rest] = existingDelayReason.reason.split(':');
      return {
        id,
        description: rest.length ? rest.join(':') : undefined,
      };
    }
    return optimisticDelayReason;
  }, [existingDelayReason?.reason, optimisticDelayReason]);

  const delayReasonSkipped = existingDelayReason?.skipped ?? optimisticDelayReasonSkipped ?? false;
  const delayReasonId = existingDelayReason?.id ?? lastCreatedDelayReasonId;
  const delayStartedAt =
    existingDelayReason?.delayStartedAt ?? delayRequiredInfo?.delayStartedAt ?? new Date().toISOString();

  const delayDurationSeconds = useMemo(() => {
    const patient = room?.patient;
    if (!patient) return delayRequiredInfo?.elapsedSeconds ?? 0;
    const entered = patientEnteredTime(patient, room);
    const procStart = eventTime(PROCEDURE_STATUSES.ONGOING, patient);
    if (!entered || !procStart) return delayRequiredInfo?.elapsedSeconds ?? 0;
    const enteredMs = typeof entered === 'string' ? new Date(entered).getTime() : entered.getTime();
    const procStartMs = typeof procStart === 'string' ? new Date(procStart).getTime() : procStart.getTime();
    return Math.max(0, Math.floor((procStartMs - enteredMs) / 1000));
  }, [room?.patient, room?.id, delayRequiredInfo?.elapsedSeconds]);

  const shouldAutoShowDelayReasonModal =
    !!delayRequiredInfo?.required &&
    !existingDelayReason?.submittedAt &&
    !delayReason &&
    !delayReasonSkipped;
  const showDelayReasonModal = shouldAutoShowDelayReasonModal || userRequestedDelayReasonModal;

  useEffect(() => {
    setOptimisticDelayReason(null);
    setOptimisticDelayReasonSkipped(null);
    setLastCreatedDelayReasonId(null);
    setUserRequestedDelayReasonModal(false);
  }, [procedureId]);

  useEffect(() => {
    const fetchAndStoreSteps = async (patientId: number) => {
      try {
        const { data } = await client.query({
          query: orTabletProcedureSteps,
          variables: { patientId },
        });

        if (data?.orTabletProcedureSteps) {
          setPreviousState(prev =>
            prev
              ? {
                ...prev,
                steps: data.orTabletProcedureSteps,
              }
              : null
          );
        }
      } catch (error) {
        console.error('Failed to fetch procedure steps:', error);
      }
    };

    if (roomPatientId) {
      const isFlipRoomActive = isFlipRoomFn(getLogEntries(room.patient));

      setPreviousState({
        patient: room.patient as ExtendedPatient,
        steps: [],
        status: room.status as PatientStatus,
        timestamp: Date.now(),
        isFlipRoomActive,
      });
      fetchAndStoreSteps(roomPatientId).then();
    }
  }, [room?.patient, roomPatientId, room?.status, client]);

  useEffect(() => {
    if (!previousState) {
      setIsWithinTimeWindow(false);
      return;
    }

    setIsWithinTimeWindow(true);
    const timeoutId = setTimeout(() => {
      setIsWithinTimeWindow(false);
    }, MOVE_BACK_WINDOW);

    return () => clearTimeout(timeoutId);
  }, [previousState]);

  const canMoveBack = useMemo(() => previousState && isWithinTimeWindow, [previousState, isWithinTimeWindow]);

  const movePatientBack = async () => {
    if (!previousState?.patient?.id || !canMoveBack || isMovingPatientBack) {
      return;
    }

    setIsMovingPatientBack(true);

    try {
      const setRoomResult = await setRoomForPatient({
        variables: {
          id: previousState.patient.id,
          roomId,
        },
      });

      if (setRoomResult?.data?.setRoomForPatient === false) {
        toaster.warning('Room change is already in progress for this patient. Please try again.');
        return;
      }

      if (previousState.patient.orRoomTimes) {
        try {
          await editOrTimes({
            variables: {
              patientId: previousState.patient.id,
              roomId,
              enteredAt: previousState.patient.orRoomTimes.enteredAt,
              timeoutStart: previousState.patient.orRoomTimes.timeoutStart,
              procedureStart: previousState.patient.orRoomTimes.procedureStart,
              calledNextPatient: previousState.patient.orRoomTimes.calledNextPatient,
              closingStart: previousState.patient.orRoomTimes.closingStart,
              surgeonLeft: previousState.patient.orRoomTimes.surgeonLeft,
            },
          });
        } catch (err) {
          console.error('Failed to restore OR times:', err);
        }
      }

      if (previousState.steps.length > 0) {
        const sortedSteps = [...previousState.steps].sort((a, b) => a.order - b.order);
        const completedSteps = sortedSteps.filter(step => step.timestamp);

        // Conservative batch size to prevent DB connection pool exhaustion
        const BATCH_SIZE = 2;

        for (let i = 0; i < completedSteps.length; i += BATCH_SIZE) {
          const batch = completedSteps.slice(i, i + BATCH_SIZE);

          try {
            await Promise.all(
              batch.map(step =>
                setProcedureStepTimestamp({
                  variables: {
                    procedureStepId: step.id,
                    procedureStepType: step.type,
                    patientId: previousState.patient.id,
                    timestamp: step.timestamp,
                  },
                }).catch(err => {
                  console.error(`Failed to restore step ${step.id}:`, err);
                  return null;
                })
              )
            );

            if (i + BATCH_SIZE < completedSteps.length) {
              await new Promise(resolve => setTimeout(resolve, 100));
            }
          } catch (err) {
            console.error('Batch processing error:', err);
          }
        }
      }

      await setStatus({
        variables: {
          id: previousState.patient.id,
          status: previousState.status,
        },
        refetchQueries: [{ query: orTabletProcedureSteps, variables: { patientId: previousState.patient.id } }],
      });

      if (previousState.isFlipRoomActive) {
        try {
          await doFlipRoom({
            variables: {
              id: previousState.patient.id,
            },
          });
        } catch (err) {
          console.error('Failed to restore flip room status (non-critical):', err);
        }
      }

      setPreviousState(null);
    } catch (error) {
      console.error('Critical error moving patient back:', error);
      alert('Failed to move patient back. Please try again or contact support.');
    } finally {
      setIsMovingPatientBack(false);
    }
  };

  const enabledProcedureSteps = useQuery(procedureSteps);
  const hasOrRoomReadyStep = enabledProcedureSteps?.data?.procedureSteps?.some(
    (step: { type: string }) => step.type === 'OR_ROOM_READY'
  );

  const proceduresData = useSubscription(proceduresSubscription, {
    variables: {
      date: format(new Date(), 'YYYY-MM-DD'),
      operationRoomId: roomId,
      isCanceled: false,
    },
    shouldResubscribe: true,
  });

  const [nextProcedure, nextProceduresInOperationRoom] = useMemo(
    () => getNextScheduledProcedures(getProcedureCards(proceduresData) || []),
    [proceduresData]
  );

  const [nextRemainingProcedure, nextRemainingProceduresInOperationRoom] = useMemo(
    () => getRemainingScheduledProcedures(getProcedureCards(proceduresData) || [], room?.procedureStartedAt),
    [proceduresData, room?.procedureStartedAt]
  );

  const [working, setWorking] = useState<boolean>(false);

  const withWorking = (fn: any) => async (args: any) => {
    if (fn) {
      setWorking(true);
      await fn(args);
      setWorking(false);
    }
  };

  const { patient, status } = room || {};

  const [localOrRoomReady, setLocalOrRoomReady] = useState<boolean>(false);

  useEffect(() => {
    setLocalOrRoomReady(!!room?.orRoomReady);
  }, [room?.orRoomReady]);

  const movePatientToOR = (roomId: number) => (patientId: number) => async () => {
    if (roomId == null || patientId == null) {
      console.warn('[movePatientToOR] Invalid parameters:', { roomId, patientId });
      return;
    }
    try {
      const result = await setRoomForPatient({ variables: { id: patientId, roomId } });
      if (result?.data?.setRoomForPatient === false) {
        toaster.warning('Room change is already in progress for this patient. Please try again.');
      }
    } catch (e) {
      console.error('[movePatientToOR] Error:', e);
    }
  };

  const changeStatus = async (status: PatientStatus) => {
    if (!status) {
      return;
    }
    await setStatus({
      variables: {
        id: patient?.id,
        status,
      },
      refetchQueries: [{ query: orTabletProcedureSteps, variables: { patientId: patient?.id } }],
    });
  };

  const notifyPhysician = async () => {
    await changeStatus(PatientStatus.Ready);
  };

  const manuallyExitProcedure = (id?: number) => async () => {
    try {
      if (!id) {
        return;
      }
      await exitProcedure({ variables: { id } });
    } catch (error) {
      console.error(error);
    } finally {
      setModal(false);
    }
  };

  const startProcedureClosing = async () => {
    try {
      await changeStatus(PatientStatus.Closing);
    } catch (error) {
      console.error(error);
    } finally {
      setClosingModal(false);
    }
  };

  const [skipCleaning] = useMutation(skipCleaningMutation);
  const [turnoverClockOff] = useMutation(turnoverClockOffMutation);

  const updateSkipCleaning = useCallback(
    async (id?: number) => {
      if (!id) {
        return;
      }

      await skipCleaning({ variables: { id } });
    },
    [skipCleaning]
  );

  const updateTurnoverClockOff = useCallback(
    async (id?: number) => {
      if (!id) {
        return;
      }

      await turnoverClockOff({ variables: { id } });
    },
    [turnoverClockOff]
  );

  const updateOrRoomReady = async (id?: number) => {
    if (!id) {
      return;
    }
    await orRoomReady({ variables: { id } });
  };

  const [isMarkingReady, setIsMarkingReady] = useState(false);

  const handleMarkRoomReady = async () => {
    if (isMarkingReady || localOrRoomReady) return;

    setIsMarkingReady(true);
    try {
      await updateOrRoomReady(roomId);

      if (roomId) {
        const cacheId = client.cache.identify({ __typename: 'Room', id: roomId });

        if (cacheId) {
          client.cache.modify({
            id: cacheId,
            fields: {
              orRoomReady() {
                return true;
              },
              orRoomReadyAt(existing) {
                return existing ?? new Date().toISOString();
              },
            },
          });
        }
      }

      setLocalOrRoomReady(true);
    } catch (error) {
      console.error('Failed to mark room ready:', error);
    } finally {
      setIsMarkingReady(false);
    }
  };
  const hasNextRemainingProcedure = Boolean(nextRemainingProcedure);
  const isAwaitingCleanup = getNestedValue('awaitingCleanup', room) === true;
  const hasTurnoverStarted = getNestedValue('turnoverStartedAt', room) !== null;

  useEffect(() => {
    if (proceduresData.loading || hasNextRemainingProcedure) {
      return;
    }

    if (!isAwaitingCleanup && !hasTurnoverStarted) {
      return;
    }

    const runUpdates = async () => {
      try {
        if (isAwaitingCleanup) {
          await updateSkipCleaning(roomId);
        }

        if (hasTurnoverStarted) {
          await updateTurnoverClockOff(roomId);
        }
      } catch (error) {
        console.error('Failed to update turnover state:', error);
      }
    };

    void runUpdates();
  }, [
    proceduresData.loading,
    hasNextRemainingProcedure,
    isAwaitingCleanup,
    hasTurnoverStarted,
    roomId,
    updateSkipCleaning,
    updateTurnoverClockOff,
  ]);

  const currentProcedureExists = [
    PatientStatus.InOr,
    PatientStatus.AnestheticStart,
    PatientStatus.ReadyForSurgery,
    PatientStatus.Ready,
    PatientStatus.TimeOut,
    PatientStatus.TimeOut2,
    PatientStatus.PhysicianIn,
    PatientStatus.Ongoing,
    PatientStatus.CallNextPatient,
    PatientStatus.Closing,
    PatientStatus.SurgeonLeftOR,
    PatientStatus.DressingOn,
    PatientStatus.ProcedureEnd,
    PatientStatus.AnestheticEnd,
    ROOM_STATUSES.CLEANING,
  ].includes(status);

  const classes = useStyles();
  const { enabledReturnPatient } = useScreenConfiguration();

  return (
    <ThemeProvider theme={tabletThemeDark}>
      {showDelayReasonModal && (
        <DelayReasonModal
          key={procedureId}
          onSubmit={async (reason, otherDescription) => {
            const reasonValue = otherDescription ? `${reason}:${otherDescription}` : reason;

            try {
              if (delayReasonId) {
                await doUpdateDelayReason({
                  variables: { id: delayReasonId, reason: reasonValue },
                });
              } else {
                const result = await doSubmitDelayReason({
                  variables: {
                    procedureId,
                    patientId: roomPatientId,
                    operationRoomId: roomId,
                    reason: reasonValue,
                    delayStartedAt,
                  },
                });
                if (result.data?.submitDelayReason?.id) {
                  setLastCreatedDelayReasonId(result.data.submitDelayReason.id);
                }
              }
              setOptimisticDelayReason({ id: reason, description: otherDescription });
              setOptimisticDelayReasonSkipped(false);
              setUserRequestedDelayReasonModal(false);
              refetchExistingDelayReason();
            } catch (error) {
              console.error('[ORTabletV2] Failed to submit delay reason:', error);
              toaster.error('Failed to save delay reason. Please try again.');
            }
          }}
          onSkip={async () => {
            try {
              if (!delayReasonId) {
                const result = await doSkipDelayReason({
                  variables: {
                    procedureId,
                    patientId: roomPatientId,
                    operationRoomId: roomId,
                    delayStartedAt,
                  },
                });
                if (result.data?.skipDelayReason?.id) {
                  setLastCreatedDelayReasonId(result.data.skipDelayReason.id);
                }
              }
              setOptimisticDelayReason(null);
              setOptimisticDelayReasonSkipped(true);
              setUserRequestedDelayReasonModal(false);
              refetchExistingDelayReason();
            } catch (error) {
              console.error('[ORTabletV2] Failed to skip delay reason:', error);
              setOptimisticDelayReason(null);
              setOptimisticDelayReasonSkipped(true);
              setUserRequestedDelayReasonModal(false);
            }
          }}
          physicianName={delayRequiredInfo?.physician || room?.patient?.physician?.name}
          patientName={delayRequiredInfo?.patientName || room?.patient?.name}
          gracePeriodMinutes={delayRequiredInfo?.gracePeriodMinutes ?? 5}
          delayDurationSeconds={delayDurationSeconds}
          delayReasons={configuredDelayReasons}
          existingReason={existingDelayReason?.reason?.split(':')[0] ?? null}
          existingOtherDescription={
            existingDelayReason?.reason?.includes(':') ? existingDelayReason.reason.split(':').slice(1).join(':') : null
          }
        />
      )}
      {patientsModal ? (
        <Root>
          <PatientSelection
            handleCancel={() => setPatientsModal(false)}
            handlePatientClick={movePatientToOR(roomId)}
            style={{ overflow: 'auto' }}
            withRoom={true}
          />
        </Root>
      ) : patientsTodayModal ? (
        <Root>
          <PatientsTodaySelection
            handleCancel={() => setPatientsTodayModal(false)}
            style={{ overflow: 'auto' }}
            withRoom={true}
            room={room}
          />
        </Root>
      ) : !status ? (
        <Box className={classes.root}>
          <Idle>
            <AirFiltrationIndicatorTimer roomId={roomId} style={{ position: 'absolute', left: '3rem', top: '2rem' }} />
            <ActionWrapper>
              <Troubleshooting roomName={roomData?.data?.tabletApp?.name} />
            </ActionWrapper>
            <Illustration src={waiting} />
            <Supertitle>{room.name}</Supertitle>
            <Title style={{ margin: 0 }}>Waiting for patient</Title>
            <Subtitle style={{ marginBottom: '1em' }}>
              Once the patient enters the room, this screen will automatically show the controls.
            </Subtitle>

            {!!hasOrRoomReadyStep && !!room?.turnoverStartedAt && (
              <ActionsAlt>
                <MainButton
                  onClick={handleMarkRoomReady}
                  disabled={isMarkingReady || localOrRoomReady}
                  style={localOrRoomReady ? { backgroundColor: '#4CAF50' } : {}}
                >
                  <WithWorking working={isMarkingReady} size={'4rem'}>
                    <Box fontSize={'6.5em'}>
                      {localOrRoomReady ? <CheckIcon fontSize="inherit" /> : <ChevronRightIcon fontSize={'inherit'} />}
                    </Box>
                  </WithWorking>
                  <ButtonLabel>{localOrRoomReady ? 'ROOM READY' : 'MARK ROOM READY'}</ButtonLabel>
                </MainButton>
              </ActionsAlt>
            )}
            {!!nextProcedure && (
              <>
                <Divider />

                <Box display="flex" flexDirection="column" justifyContent="center" p={2} width="100%">
                  <Box mb={2}>
                    <Typography align="center" variant="h6" gutterBottom>
                      Upcoming Procedure
                    </Typography>
                  </Box>
                  <Box display="block" className={classes.dialogContent} width="100%">
                    {/* @ts-ignore */}
                    <UpcomingProcedure
                      procedure={nextProcedure}
                      isUnassigned={false}
                      date={new Date()}
                      isPatientIconVisible={true}
                      vendorColor="secondary"
                    />
                  </Box>
                </Box>
              </>
            )}
            <Box display="flex" justifyContent="center" mt={4}>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setPatientsModal(true)}
                style={{
                  padding: '12px 24px',
                  fontSize: '1.1rem',
                  textTransform: 'none',
                  borderRadius: '8px',
                  marginRight: '48px',
                }}
              >
                <Box display="flex" alignItems="center">
                  <PersonOutlineIcon />
                  <span>Patient not Detected? Tap Here.</span>
                </Box>
              </Button>

              <Button
                variant="outlined"
                color="secondary"
                onClick={() => setPatientsTodayModal(true)}
                style={{
                  padding: '12px 24px',
                  fontSize: '1.1rem',
                  textTransform: 'none',
                  borderRadius: '8px',
                }}
              >
                <Box display="flex" alignItems="center">
                  <TodayIcon />
                  <span>View Previous Patients</span>
                </Box>
              </Button>
            </Box>

            {canMoveBack && !!previousState && (
              <Box display="flex" justifyContent="center">
                {enabledReturnPatient && (
                  <MoveBackButton
                    variant="outlined"
                    color="secondary"
                    onClick={movePatientBack}
                    disabled={isMovingPatientBack}
                  >
                    <WithWorking working={isMovingPatientBack} size="1.5rem" spinnerStyle={{ marginTop: '-0.5em' }}>
                      <Box display="flex" alignItems="center">
                        <ArrowBackIcon style={{ marginRight: '8px' }} />
                        <PersonOutlineIcon style={{ marginRight: '8px' }} />
                        <span>Move {previousState.patient.name || 'Patient'} Back</span>
                      </Box>
                    </WithWorking>
                  </MoveBackButton>
                )}
              </Box>
            )}
          </Idle>
        </Box>
      ) : modal ? (
        <Root>
          <ExitModal
            handleConfirm={withWorking(manuallyExitProcedure(patient?.id))}
            handleCancel={() => setModal(false)}
            working={working}
          />
        </Root>
      ) : closingModal ? (
        <Root>
          <ClosingModal
            handleConfirm={startProcedureClosing}
            handleCancel={() => setClosingModal(false)}
            working={working}
          />
        </Root>
      ) : currentProcedureExists ? (
        <CurrentProcedure
          nextProcedure={nextProcedure}
          roomId={roomId}
          room={room}
          working={working}
          withWorking={withWorking}
          nextProceduresInOperationRoom={nextProceduresInOperationRoom}
          nextRemainingProcedure={nextRemainingProcedure}
          notifyPhysician={notifyPhysician}
          loading={proceduresData.loading}
          initialFlipRoomState={previousState?.isFlipRoomActive}
          delayReasonLabel={
            delayReason
              ? delayReason.id.toLowerCase() === 'other' && delayReason.description
                ? delayReason.description
                : getDelayReasonLabel(delayReason.id, configuredDelayReasons)
              : undefined
          }
          onDelayReasonClick={() => setUserRequestedDelayReasonModal(true)}
          delayReasonSkipped={delayReasonSkipped}
        />
      ) : null}
      {!patientsModal && !patientsTodayModal && !status && !canMoveBack && <ClientUpdater />}
      {canMoveBack && !!previousState && status === ROOM_STATUSES.CLEANING && (
        <Box display="flex" justifyContent="center" style={{ position: 'absolute', bottom: '2rem', left: 0, right: 0 }}>
          {!!enabledReturnPatient && (
            <MoveBackButton
              variant="outlined"
              color="secondary"
              onClick={movePatientBack}
              disabled={isMovingPatientBack}
            >
              <WithWorking working={isMovingPatientBack} size="1.5rem" spinnerStyle={{ marginTop: '-0.5em' }}>
                <Box display="flex" alignItems="center">
                  <ArrowBackIcon style={{ marginRight: '8px' }} />
                  <PersonOutlineIcon style={{ marginRight: '8px' }} />
                  <span>Move {previousState.patient.name || 'Patient'} Back</span>
                </Box>
              </WithWorking>
            </MoveBackButton>
          )}
        </Box>
      )}
    </ThemeProvider>
  );
};

OperationRoomTabletV2.defaultProps = {
  room: {},
};

OperationRoomTabletV2.propTypes = {
  room: PropTypes.object.isRequired,
};

export default OperationRoomTabletV2;
